import React from 'react';
import { createPortal } from 'react-dom';
import { getDisplayStatus, formatEventDate, parseDate, businessToday } from '../utils/date-utils';
import { useGeocodeCityState } from '../utils/geocode';
import TransportChips from '../components/TransportChips';
import FadeIn from '../components/ui/FadeIn';

export const STATUS_COLORS: Record<string, string> = {
  pending:   'bg-[rgb(95_95_95/0.2)] day:bg-gray-100 text-on-surface-variant day:text-gray-700 ring-[rgb(95_95_95/0.3)] day:ring-gray-300',
  confirmed: 'bg-blue-500/20 day:bg-blue-100 text-blue-400 day:text-blue-700 ring-blue-500/30 day:ring-blue-300',
  packing:   'bg-purple-500/20 day:bg-purple-100 text-purple-400 day:text-purple-700 ring-purple-500/30 day:ring-purple-300',
  active:    'bg-green-500/20 day:bg-green-100 text-green-400 day:text-green-700 ring-green-500/30 day:ring-green-300',
  completed: 'bg-emerald-500/20 day:bg-emerald-100 text-emerald-400 day:text-emerald-700 ring-emerald-500/30 day:ring-emerald-300',
  cancelled: 'bg-red-500/20 day:bg-red-100 text-red-400 day:text-red-700 ring-red-500/30 day:ring-red-300',
};

export type ShippingZone = {
  label: 'RED' | 'YELLOW' | 'GREEN' | 'PAST';
  color: string;
};

/**
 * The last day a show occupies. A single-day show has no `end_date`, so it ends
 * the day it starts. Exported because "when is this over" has to mean one thing
 * across the pill, the row, the map pin and the zone — they used to disagree.
 */
export const showEndDate = (event: { event_date?: string; end_date?: string | null }): string =>
  (event.end_date || event.event_date) as string;

/** Is the show finished? Asked of the END, never the start. */
export const isShowPast = (
  event: { event_date?: string; end_date?: string | null },
  today = businessToday()
): boolean => parseDate(showEndDate(event))!.isBefore(today);

// Both sides of this subtraction are calendar dates on the Eastern business
// calendar, never moments. A raw `dayjs(eventDate).diff(dayjs(), 'day')` mixed a
// UTC-parsed date-only field with the viewer's local wall clock, so the zone a
// show sat in changed with the reader's timezone and with the hour of the day.
//
// TWO DATES, TWO QUESTIONS. "Is it over?" is asked of `end_date`, so a show
// running Aug 8-12 is not past on the 10th. "How urgent is it?" is asked of
// `event_date`, because the shipping window closes when the show STARTS, not
// when it ends. Measuring urgency from the end would let a 5-day show read GREEN
// while its trucks were due to leave.
export const getShippingZone = (
  eventDate: string,
  endDate?: string | null
): ShippingZone => {
  const today = businessToday();
  if (parseDate(endDate || eventDate)!.isBefore(today)) {
    return { label: 'PAST', color: 'bg-[rgb(95_95_95/0.2)] day:bg-gray-100 text-on-surface-variant day:text-gray-700 ring-[rgb(95_95_95/0.3)] day:ring-gray-300' };
  }
  // Already started but not finished. Negative days would fall through to PAST
  // below, which is what used to gray out a show that was happening that day.
  // Clamped to the most urgent zone rather than given a fifth label, which would
  // mean touching the group order, the stroke colors and the filter chips.
  const days = Math.max(0, parseDate(eventDate)!.diff(today, 'day'));
  if (days < 7)  return { label: 'RED',    color: 'bg-red-500/20 day:bg-red-100 text-red-400 day:text-red-700 ring-red-500/30 day:ring-red-300' };
  if (days < 14) return { label: 'YELLOW', color: 'bg-yellow-500/20 day:bg-yellow-100 text-yellow-400 day:text-yellow-700 ring-yellow-500/30 day:ring-yellow-300' };
  return { label: 'GREEN', color: 'bg-green-500/20 day:bg-green-100 text-green-400 day:text-green-700 ring-green-500/30 day:ring-green-300' };
};

const VALID_STATE_ABBRS = new Set([
  'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA',
  'KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ',
  'NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT',
  'VA','WA','WV','WI','WY','DC',
]);

const STATE_TO_ABBR: Record<string, string> = {
  Alabama: 'AL', Alaska: 'AK', Arizona: 'AZ', Arkansas: 'AR', California: 'CA',
  Colorado: 'CO', Connecticut: 'CT', Delaware: 'DE', Florida: 'FL', Georgia: 'GA',
  Hawaii: 'HI', Idaho: 'ID', Illinois: 'IL', Indiana: 'IN', Iowa: 'IA',
  Kansas: 'KS', Kentucky: 'KY', Louisiana: 'LA', Maine: 'ME', Maryland: 'MD',
  Massachusetts: 'MA', Michigan: 'MI', Minnesota: 'MN', Mississippi: 'MS', Missouri: 'MO',
  Montana: 'MT', Nebraska: 'NE', Nevada: 'NV', 'New Hampshire': 'NH', 'New Jersey': 'NJ',
  'New Mexico': 'NM', 'New York': 'NY', 'North Carolina': 'NC', 'North Dakota': 'ND',
  Ohio: 'OH', Oklahoma: 'OK', Oregon: 'OR', Pennsylvania: 'PA', 'Rhode Island': 'RI',
  'South Carolina': 'SC', 'South Dakota': 'SD', Tennessee: 'TN', Texas: 'TX', Utah: 'UT',
  Vermont: 'VT', Virginia: 'VA', Washington: 'WA', 'West Virginia': 'WV', Wisconsin: 'WI',
  Wyoming: 'WY', 'District of Columbia': 'DC',
};

// Pulls "City, ST" out of a free-form address. Handles two coexisting formats:
//   1. Google/Monday short form: "123 Main St, Detroit, MI 48226, USA" — 2-letter state code.
//   2. OSM/Nominatim long form:  "..., Charleston, Charleston County, South Carolina, 29401, United States" — full state name + county token.
// For the long form, we walk back from the state and skip "County" / numeric tokens (ZIPs, suite numbers)
// to find the actual city, otherwise we'd return "Charleston County" as the city.
// Returns null when no recognizable US state appears (foreign addresses, single-string venues, "TBD").
export const parseCityState = (location?: string | null): { city: string; state: string } | null => {
  if (!location) return null;

  // Format 1 — short 2-letter state code (gated to actual US states so "Toronto, ON" / "London, UK" don't false-match).
  const shortMatch = location.match(/(?:^|,)\s*([^,]+?),\s*([A-Z]{2})\b/);
  if (shortMatch && VALID_STATE_ABBRS.has(shortMatch[2])) {
    return { city: shortMatch[1].trim(), state: shortMatch[2] };
  }

  // Format 2 — full state name. Walk right-to-left until we hit a known state, then walk further
  // back skipping county / numeric / regional-subdivision tokens to find the city.
  const tokens = location.split(',').map(t => t.trim()).filter(Boolean);
  for (let i = tokens.length - 1; i >= 0; i--) {
    const stateName = tokens[i];
    if (stateName in STATE_TO_ABBR) {
      const stateLower = stateName.toLowerCase();
      let city: string | null = null;
      for (let j = i - 1; j >= 0; j--) {
        const t = tokens[j];
        if (/^[\d\-]+$/.test(t)) continue;                                  // ZIP / suite / pure number
        if (/\bCounty$/i.test(t)) continue;                                  // "Charleston County"
        if (t !== stateName && t.toLowerCase().includes(stateLower)) continue; // "East Tennessee", "Middle Tennessee"
        city = t;
        break;
      }
      if (city) return { city, state: STATE_TO_ABBR[stateName] };
    }
  }
  return null;
};

type HoverEvent = {
  id?: string | number;
  name?: string;
  event_date?: string;
  end_date?: string;
  status?: string;
  location?: string | null;
  launch_latitude?: number | null;
  launch_longitude?: number | null;
  ls_drone_count?: number;
  pyro_drone_count?: number;
  tour_id?: string | number | null;
  _isLease?: boolean;
  _isRental?: boolean;
};

/** A show's transport, for the chips. `hasVehicle` is an event_vehicle row. */
export type HoverTransport = {
  hasVehicle: boolean;
  transportArranged: boolean;
  transportMode: string | null;
};

/** One person on the hovered show. `shifts` are the days they cover; empty is
 *  legitimate (days not set yet) and renders as a name with no days. */
export type HoverCrew = {
  user_id: string;
  name: string;
  role: string | null;
  shifts: { start_date: string; end_date: string }[];
};

const MAX_CREW_SHOWN = 6;

// "Jun 12" · "Jun 12-14" · "Jun 30 - Jul 2". Hyphens, not en/em dashes.
const crewDayText = (shifts: HoverCrew['shifts']): string =>
  shifts.map(s => {
    const start = formatEventDate(s.start_date, 'MMM D');
    if (!s.end_date || s.end_date === s.start_date) return start;
    const sameMonth = String(s.start_date).slice(0, 7) === String(s.end_date).slice(0, 7);
    return sameMonth
      ? `${start}-${formatEventDate(s.end_date, 'D')}`
      : `${start} - ${formatEventDate(s.end_date, 'MMM D')}`;
  }).join(', ');

type LeaseRentalSpecial = 'lease' | 'rental' | null;

const getSpecial = (event: HoverEvent): LeaseRentalSpecial =>
  event._isLease ? 'lease' : event._isRental ? 'rental' : null;

const SPECIAL_PILL: Record<NonNullable<LeaseRentalSpecial>, string> = {
  lease:  'bg-blue-500/20 day:bg-blue-100 text-blue-400 day:text-blue-700 ring-blue-500/30 day:ring-blue-300',
  rental: 'bg-orange-500/20 day:bg-orange-100 text-orange-400 day:text-orange-700 ring-orange-500/30 day:ring-orange-300',
};

export const EventHoverCardContent: React.FC<{
  event: HoverEvent;
  geocodeEnabled?: boolean;
  showDate?: boolean;
  /** Opt-in. Surfaces that have crew data pass it; the calendar and the map
   *  tooltips do not, and get the card exactly as before. */
  crew?: HoverCrew[];
  /** Opt-in, same deal: only surfaces that know a show's vehicle assignments can
   *  answer "driving", since that is an event_vehicle row rather than a column. */
  transport?: HoverTransport;
}> = ({ event, geocodeEnabled = true, showDate = true, crew, transport }) => {
  const special = getSpecial(event);
  const displayStatus = special ?? getDisplayStatus(event);
  const zone = event.event_date ? getShippingZone(event.event_date, event.end_date) : null;
  const parsed = parseCityState(event.location);
  // Fallback: when address text didn't yield a "City, ST", reverse-geocode the coords.
  // Hook is gated by `geocodeEnabled` so leaflet's eager-mounted tooltips don't fire 50 lookups on page load.
  const geocoded = useGeocodeCityState(
    event.launch_latitude,
    event.launch_longitude,
    geocodeEnabled && !parsed
  );
  const cityState = parsed || (geocoded ? { city: geocoded.city || '', state: geocoded.state || '' } : null);
  const cityStateText = cityState
    ? (cityState.city && cityState.state ? `${cityState.city}, ${cityState.state}` : cityState.city || cityState.state)
    : null;
  // Distinguish "not yet fetched" (undefined) from "fetched, no result" (null).
  // We only show the loading placeholder while a fetch is actually in flight.
  const isGeocodeLoading =
    !parsed &&
    geocodeEnabled &&
    event.launch_latitude != null &&
    event.launch_longitude != null &&
    geocoded === undefined;
  const droneCount = (event.ls_drone_count || 0) + (event.pyro_drone_count || 0);
  const pillClass = special ? SPECIAL_PILL[special] : (STATUS_COLORS[displayStatus] || '');

  return (
    <div className="space-y-1 min-w-[160px]">
      <div className="font-bold text-on-surface day:text-gray-900">{event.name || 'Untitled'}</div>
      {droneCount > 0 && (
        <div className="text-[11px] text-primary day:text-amber-700 tabular-nums font-medium">
          {droneCount} drones
          {event.tour_id && <span className="text-cyan-400/70 day:text-cyan-700 ml-2">• Tour</span>}
        </div>
      )}
      {droneCount === 0 && event.tour_id && (
        <div className="text-[11px] text-cyan-400/70 day:text-cyan-700">• Tour</div>
      )}
      {showDate && event.event_date && (
        <div className="text-[11px] text-on-surface-variant day:text-gray-600">{formatEventDate(event.event_date, 'MMM D, YYYY')}</div>
      )}
      {cityStateText ? (
        <FadeIn key="place"><div className="text-[11px] text-on-surface day:text-gray-700">{cityStateText}</div></FadeIn>
      ) : isGeocodeLoading ? (
        <div className="text-[11px] text-on-surface-variant day:text-gray-500 italic">Loading location…</div>
      ) : null}
      <div className="flex gap-1 flex-wrap pt-0.5">
        <span className={`inline-flex px-1.5 py-0.5 rounded text-[10px] font-medium capitalize ring-1 ring-inset ${pillClass}`}>
          {displayStatus}
        </span>
        {zone && !special && (
          <span className={`inline-flex px-1.5 py-0.5 rounded text-[10px] font-bold ring-1 ring-inset ${zone.color}`}>
            {zone.label}
          </span>
        )}
        {transport && (
          <TransportChips
            hasVehicle={transport.hasVehicle}
            transportArranged={transport.transportArranged}
            transportMode={transport.transportMode}
            size="compact"
          />
        )}
      </div>
      {crew && crew.length > 0 && (
        <div className="pt-1.5 mt-1 border-t border-gray-700/60 day:border-gray-200">
          <div className="text-[10px] uppercase tracking-wide text-on-surface-variant day:text-gray-500 mb-0.5">
            Crew
          </div>
          {crew.slice(0, MAX_CREW_SHOWN).map(c => {
            const days = crewDayText(c.shifts);
            return (
              <div key={c.user_id} className="flex items-baseline gap-1.5 text-[11px] leading-snug">
                <span className="text-on-surface day:text-gray-800 truncate max-w-[110px]">{c.name}</span>
                {c.role && <span className="text-on-surface-variant day:text-gray-500 shrink-0">{c.role}</span>}
                <span className="ml-auto shrink-0 tabular-nums text-on-surface-variant day:text-gray-600">
                  {days || 'no days set'}
                </span>
              </div>
            );
          })}
          {crew.length > MAX_CREW_SHOWN && (
            <div className="text-[10px] text-on-surface-variant day:text-gray-500 pt-0.5">
              and {crew.length - MAX_CREW_SHOWN} more
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Mouse-positioned portal hover card for surfaces that aren't Leaflet maps (e.g. the calendar).
// Clamps horizontally to the viewport and flips below the cursor when there isn't enough room above.
// boxTop/boxBottom (optional): the viewport-relative top/bottom of the hovered
// element. When present (grid bands) the card anchors to the box so it never
// covers it; when absent (calendar etc.) it falls back to cursor anchoring.
export type HoverCardState = {
  event: HoverEvent;
  x: number;
  y: number;
  boxTop?: number;
  boxBottom?: number;
  crew?: HoverCrew[];
  transport?: HoverTransport;
} | null;

const CARD_WIDTH_GUESS = 220; // rough max — content is min-w-[160px], padded to ~200px in practice
const CARD_WIDTH_WITH_CREW = 268; // matches the max-w below, so the clamp stays honest
const CARD_BASE_HEIGHT = 104; // rough card height, for the above/below flip decision
const CREW_HEADER_HEIGHT = 22;
const CREW_ROW_HEIGHT = 15;
const VIEWPORT_PAD = 12;

// The flip decision needs the card's real height, and a crew list can double it.
// A stale guess here does not misplace the card by a little, it renders it off the
// top of the viewport — the band-anchored branch places the card's BOTTOM at the
// band's top edge, so underestimating pushes the whole thing above the fold.
const cardHeight = (crew?: HoverCrew[]): number => {
  if (!crew || crew.length === 0) return CARD_BASE_HEIGHT;
  const shown = Math.min(crew.length, MAX_CREW_SHOWN);
  return CARD_BASE_HEIGHT + CREW_HEADER_HEIGHT + shown * CREW_ROW_HEIGHT
    + (crew.length > MAX_CREW_SHOWN ? 14 : 0);
};

export const EventHoverCardPortal: React.FC<{ hover: HoverCardState }> = ({ hover }) => {
  if (!hover) return null;

  const hasCrew = !!hover.crew && hover.crew.length > 0;
  const widthGuess = hasCrew ? CARD_WIDTH_WITH_CREW : CARD_WIDTH_GUESS;
  const CARD_HEIGHT_GUESS = cardHeight(hover.crew);

  const left = Math.max(
    VIEWPORT_PAD,
    Math.min(window.innerWidth - widthGuess - VIEWPORT_PAD, hover.x - widthGuess / 2)
  );

  // Box-anchored (grid bands): sit fully ABOVE the band's top edge, or BELOW its
  // bottom edge when there isn't room above — never over the band being hovered.
  // Cursor-anchored (calendar etc.): float just off the cursor, flipping near the top.
  let top: number;
  let transform: string;
  if (hover.boxTop != null && hover.boxBottom != null) {
    const roomAbove = hover.boxTop >= CARD_HEIGHT_GUESS + VIEWPORT_PAD;
    top = roomAbove ? hover.boxTop - 8 : hover.boxBottom + 8;
    transform = roomAbove ? 'translateY(-100%)' : 'none';
  } else {
    const flipDown = hover.y < 90; // not enough room above cursor → render below
    top = flipDown ? hover.y + 18 : hover.y - 12;
    transform = flipDown ? 'none' : 'translateY(-100%)';
  }

  return createPortal(
    <div
      className="fixed z-[10000] pointer-events-none rounded-lg bg-surface-container-high day:bg-white day:ring-1 day:ring-gray-200 shadow-overlay day:shadow-lg day:shadow-gray-900/15 px-2.5 py-1.5 max-w-[268px]"
      style={{ left, top, transform }}
    >
      <EventHoverCardContent event={hover.event} showDate={false} crew={hover.crew} transport={hover.transport} />
    </div>,
    document.body
  );
};
